import React from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext"; // adjust path
import { toast } from "sonner"; // adjust to your toast lib
import { uploadImage, addArt } from "../lib/actions"; // adjust path
import { LoaderIcon } from "lucide-react"; // adjust as needed

// ─── Styled Components ────────────────────────────────────────────────────────

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  margin: 2rem auto;
  padding: 2rem;
  border-radius: 1rem;
  max-width: 680px;
  width: 100%;

  form {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 0;
  }

  input[type="text"],
  input[type="number"] {
    position: relative;
    padding: 0.8rem;
    border-radius: 0.5rem;
    border: 1px solid ${(props) => props.theme.text};
    background-color: transparent;
    color: ${(props) => props.theme.text};
    font-size: 1rem;
    width: 100%;
    box-sizing: border-box;

    &:focus {
      outline: none;
      border-color: ${(props) => props.theme.primary};
    }
  }

  textarea {
    padding: 0.8rem;
    border-radius: 0.5rem;
    border: 1px solid ${(props) => props.theme.text};
    background-color: transparent;
    color: ${(props) => props.theme.text};
    font-size: 1rem;
    width: 100%;
    box-sizing: border-box;
    resize: vertical;
    min-height: 100px;

    &:focus {
      outline: none;
      border-color: ${(props) => props.theme.primary};
    }
  }

  audio {
    border-radius: 0.5rem;
    width: 100%;
  }

  @media (max-width: 40em) {
    width: 90%;
    padding: 1rem;
    font-size: 14px;
  }
`;

const PageTitle = styled.h2`
  font-size: 1.6rem;
  font-weight: 700;
  margin-bottom: 0.25rem;
  color: ${(props) => props.theme.text};
`;

const PageSubtitle = styled.p`
  font-size: 0.9rem;
  color: #9ca3af;
  margin-bottom: 2rem;
`;

const RequiredNote = styled.p`
  font-size: 0.85rem;
  color: #9ca3af;
  margin-bottom: 1.5rem;

  span {
    color: #ef4444;
  }
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 2rem;
`;

const SectionTitle = styled.label`
  font-size: 1rem;
  font-weight: 700;
  color: ${(props) => props.theme.text};
  margin-bottom: 0.25rem;
  display: flex;
  align-items: center;
  gap: 4px;

  .required {
    color: #ef4444;
  }
  .optional-badge {
    font-size: 0.72rem;
    font-weight: 500;
    background: rgba(156, 163, 175, 0.15);
    color: #9ca3af;
    padding: 2px 8px;
    border-radius: 999px;
    margin-left: 6px;
  }
`;

const SectionDesc = styled.p`
  font-size: 0.82rem;
  color: #9ca3af;
  margin-bottom: 0.75rem;
  line-height: 1.5;
`;

const FileDropZone = styled.div`
  border: 2px dashed ${(props) => (props.$hasFile ? "#22c55e" : "#374151")};
  border-radius: 0.75rem;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease;
  background: ${(props) =>
    props.$hasFile ? "rgba(34,197,94,0.04)" : "rgba(255,255,255,0.02)"};
  position: relative;

  &:hover {
    border-color: ${(props) => (props.$hasFile ? "#22c55e" : "#6b7280")};
    background: rgba(255, 255, 255, 0.04);
  }

  input[type="file"] {
    position: absolute;
    inset: 0;
    opacity: 0;
    cursor: pointer;
    width: 100%;
    height: 100%;
  }
`;

const DropZoneIcon = styled.div`
  font-size: 2rem;
  line-height: 1;
`;

const DropZoneText = styled.p`
  font-size: 0.9rem;
  color: ${(props) => (props.$hasFile ? "#22c55e" : "#9ca3af")};
  text-align: center;
  margin: 0;
`;

const DropZoneHint = styled.p`
  font-size: 0.75rem;
  color: #6b7280;
  text-align: center;
  margin: 0;
`;

const ChangeBtn = styled.button`
  type: button;
  margin-top: 0.5rem;
  padding: 6px 18px;
  border-radius: 999px;
  border: 1px solid #374151;
  background: transparent;
  color: #9ca3af;
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #6b7280;
    color: ${(props) => props.theme.text};
  }
`;

const MediaPreview = styled.div`
  margin-top: 1rem;
  position: relative;
  display: inline-block;

  img {
    width: 140px;
    height: 140px;
    object-fit: cover;
    border-radius: 10px;
    border: 2px solid #1f2937;
    display: block;
  }

  video,
  audio {
    width: 100%;
    border-radius: 8px;
    margin-top: 0.5rem;
  }
`;

const RemoveBtn = styled.button`
  position: absolute;
  top: -8px;
  right: -8px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #ef4444;
  color: white;
  border: none;
  font-size: 0.75rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  z-index: 1;

  &:hover {
    background: #dc2626;
  }
`;

const MediaTypeTag = styled.span`
  display: inline-block;
  margin-top: 6px;
  font-size: 0.72rem;
  padding: 2px 10px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.07);
  color: #9ca3af;
`;

const RoyaltyRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
`;

const RoyaltySlider = styled.input`
  -webkit-appearance: none;
  appearance: none;
  flex: 1;
  height: 6px;
  border-radius: 999px;
  background: linear-gradient(
    to right,
    #22c55e 0%,
    #22c55e ${(props) => props.value}%,
    #374151 ${(props) => props.value}%,
    #374151 100%
  );
  outline: none;
  cursor: pointer;
  border: none;
  min-width: 0;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: #22c55e;
    cursor: pointer;
    border: 2px solid #111;
    box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.2);
  }

  &::-moz-range-thumb {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: #22c55e;
    cursor: pointer;
    border: 2px solid #111;
  }
`;

const RoyaltyValue = styled.div`
  font-size: 1.2rem;
  font-weight: 700;
  color: #22c55e;
  min-width: 52px;
  text-align: right;
`;

const RoyaltyPresets = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-top: 0.75rem;
`;

const PresetChip = styled.button`
  padding: 4px 14px;
  border-radius: 999px;
  border: 1px solid ${(props) => (props.$active ? "#22c55e" : "#374151")};
  background: ${(props) =>
    props.$active ? "rgba(34,197,94,0.1)" : "transparent"};
  color: ${(props) => (props.$active ? "#22c55e" : "#9ca3af")};
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #22c55e;
    color: #22c55e;
  }
`;

const RoyaltyInfo = styled.p`
  font-size: 0.78rem;
  color: #6b7280;
  margin-top: 0.75rem;
  line-height: 1.5;
  padding: 0.6rem 0.8rem;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 0.5rem;
  border-left: 3px solid #374151;
`;

const ClubBadge = styled.div`
  background: rgba(0, 255, 0, 0.07);
  border: 1px solid rgba(0, 255, 0, 0.25);
  border-radius: 10px;
  padding: 12px 16px;
  margin-bottom: 2rem;
  font-size: 0.88rem;
  color: #22c55e;
  line-height: 1.6;

  small {
    color: #9ca3af;
  }
`;

const SubmitButton = styled.button`
  margin-top: 1rem;
  padding: 1rem;
  border-radius: 1rem;
  background-color: black;
  color: white;
  border: 2px solid transparent;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 600;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover:not(:disabled) {
    background-color: white;
    color: black;
    border-color: black;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid #1f2937;
  margin: 0.5rem 0 2rem;
`;

// ─── Helpers ──────────────────────────────────────────────────────────────────

const getMediaType = (file) => {
  if (!file) return null;
  if (file.type.startsWith("image/")) return "image";
  if (file.type.startsWith("video/")) return "video";
  if (file.type.startsWith("audio/")) return "audio";
  if (
    file.name.endsWith(".glb") ||
    file.name.endsWith(".gltf") ||
    file.name.endsWith(".obj")
  )
    return "3d";
  return "other";
};

const ROYALTY_PRESETS = [0, 2.5, 5, 10];
const MAX_MEDIA_SIZE_MB = 100;
const MAX_PREVIEW_SIZE_MB = 5;

// ─── Component ────────────────────────────────────────────────────────────────

export const CreateArtForm = ({ club }) => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Form state
  const [isLoading, setIsLoading] = React.useState(false);
  const [name, setName] = React.useState("");
  const [price, setPrice] = React.useState(0);
  const [description, setDescription] = React.useState("");
  const [size, setSize] = React.useState("");
  const [royalty, setRoyalty] = React.useState(5);

  // Media file (video / audio / image / 3D)
  const [mediaFile, setMediaFile] = React.useState(null);
  const [mediaPreviewUrl, setMediaPreviewUrl] = React.useState("");
  const [mediaType, setMediaType] = React.useState(null);

  // Preview image
  const [previewImgFile, setPreviewImgFile] = React.useState(null);
  const [previewImgUrl, setPreviewImgUrl] = React.useState("");

  React.useEffect(() => {
    if (club) {
      console.log("Creating art for club:", club.name, "ID:", club.$id);
    }
  }, [club]);

  // ── File handlers ──────────────────────────────────────────────────────────

  const handleMediaChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const maxBytes = MAX_MEDIA_SIZE_MB * 1024 * 1024;
    if (file.size > maxBytes) {
      toast.warning(`Media file must be smaller than ${MAX_MEDIA_SIZE_MB}MB.`);
      e.target.value = "";
      return;
    }

    const type = getMediaType(file);
    setMediaType(type);
    setMediaFile(file);

    const localUrl = URL.createObjectURL(file);
    setMediaPreviewUrl(localUrl);
  };

  const clearMedia = () => {
    setMediaFile(null);
    setMediaPreviewUrl("");
    setMediaType(null);
  };

  const handlePreviewImgChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.warning("Preview image must be an image file.");
      e.target.value = "";
      return;
    }

    const maxBytes = MAX_PREVIEW_SIZE_MB * 1024 * 1024;
    if (file.size > maxBytes) {
      toast.warning(`Preview image must be smaller than ${MAX_PREVIEW_SIZE_MB}MB.`);
      e.target.value = "";
      return;
    }

    setPreviewImgFile(file);
    setPreviewImgUrl(URL.createObjectURL(file));
  };

  const clearPreviewImg = () => {
    setPreviewImgFile(null);
    setPreviewImgUrl("");
  };

  // ── Upload helpers ─────────────────────────────────────────────────────────

  const uploadFile = async (file) => {
    if (!file) return null;
    const result = await uploadImage(file); // reuse your existing util
    if (!result || !result.previewUrl) return null;
    return result.previewUrl;
  };

  // ── Submit ─────────────────────────────────────────────────────────────────

  const submit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    const toastId = toast.loading(
      club ? `Creating art for ${club.name}...` : "Creating Art Credentials..."
    );

    // 1. Upload media
    let mediaUrl = null;
    if (mediaFile) {
      try {
        mediaUrl = await uploadFile(mediaFile);
        if (!mediaUrl) {
          toast.error("Media upload failed. Please try again.", { id: toastId });
          setIsLoading(false);
          return;
        }
      } catch {
        toast.error("Error uploading media.", { id: toastId });
        setIsLoading(false);
        return;
      }
    } else {
      toast.error("Please upload a media file.", { id: toastId });
      setIsLoading(false);
      return;
    }

    // 2. Upload preview image (required only if media is not an image)
    let previewUrl = null;
    const needsPreview = mediaType !== "image";
    if (needsPreview) {
      if (!previewImgFile) {
        toast.error("A preview image is required for non-image media.", { id: toastId });
        setIsLoading(false);
        return;
      }
      try {
        previewUrl = await uploadFile(previewImgFile);
        if (!previewUrl) {
          toast.error("Preview image upload failed.", { id: toastId });
          setIsLoading(false);
          return;
        }
      } catch {
        toast.error("Error uploading preview image.", { id: toastId });
        setIsLoading(false);
        return;
      }
    } else {
      // The media itself is the preview
      previewUrl = mediaUrl;
    }

    const formData = {
      name,
      price: parseFloat(price),
      size,
      description,
      img: mediaUrl,
      media: previewUrl,
      mediaType,
      royalty: parseFloat(royalty),
      userId: user.$id,
      ClubId: club?.$id || null,
    };

    console.log(formData)
    try {
      const art = await addArt(formData);
      toast.success(
        club ? `Art created for ${club.name} successfully!` : "Art Created Successfully!",
        { id: toastId }
      );
      setIsLoading(false);
      navigate("/mint", { state: { art: art.$id, club: club || null } });
    } catch (error) {
      console.error("Error creating art:", error);
      toast.error("Error Creating Art. Check your connection.", { id: toastId });
      setIsLoading(false);
    }
  };

  // ── Derived ────────────────────────────────────────────────────────────────

  const needsPreviewImg = mediaType && mediaType !== "image";
  const isSubmittable = name && description && mediaFile && (!needsPreviewImg || previewImgFile);

  const mediaTypeLabel = {
    image: "Image",
    video: "Video",
    audio: "Audio",
    "3d": "3D Model",
    other: "File",
  };

  return (
    <Wrapper>
      {/* ── Page header ── */}
      <PageTitle>
        {club ? `Create Artwork for ${club.name}` : "Create Artwork (NFT)"}
      </PageTitle>
      <PageSubtitle>
        {club
          ? `Fill in the details below to list your artwork in the ${club.name} club.`
          : "Fill in the details below to mint and list your NFT artwork."}
      </PageSubtitle>
      <RequiredNote>
        <span>* </span>Required fields
      </RequiredNote>

      {/* ── Club badge ── */}
      {club && (
        <ClubBadge>
          <strong>Club Context:</strong> {club.name} · {club.floorPrice} · {club.chain}
          <br />
          <small>
            Acceptance Rate: {club.acceptanceRate}% · This artwork will be associated with
            this club
          </small>
        </ClubBadge>
      )}

      <form onSubmit={submit}>

        {/* ── Section 1: Media file ── */}
        <Section>
          <SectionTitle>
            Image, Video, Audio, or 3D Model <span className="required">*</span>
          </SectionTitle>
          <SectionDesc>
            File types supported: JPG, PNG, GIF, SVG, MP4, WEBM, MP3, WAV, OGG,
            GLB, GLTF. Max size: {MAX_MEDIA_SIZE_MB} MB.
          </SectionDesc>

          {!mediaFile ? (
            <FileDropZone $hasFile={false}>
              <input
                type="file"
                accept="image/*,video/*,audio/*,.glb,.gltf,.obj"
                onChange={handleMediaChange}
                disabled={isLoading}
              />
              <DropZoneIcon>🖼️</DropZoneIcon>
              <DropZoneText $hasFile={false}>
                Drag & drop or <u>browse</u> your file
              </DropZoneText>
              <DropZoneHint>
                Images, videos, audio, and 3D models supported
              </DropZoneHint>
            </FileDropZone>
          ) : (
            <div>
              {/* Media preview based on type */}
              {mediaType === "image" && (
                <MediaPreview>
                  <img src={mediaPreviewUrl} alt="Media preview" />
                  <RemoveBtn type="button" onClick={clearMedia}>✕ </RemoveBtn>
                </MediaPreview>
              )}
              {mediaType === "video" && (
                <MediaPreview style={{ display: "block" }}>
                  <video src={mediaPreviewUrl} controls style={{ width: "100%", borderRadius: "8px", marginTop: 0 }} />
                  <RemoveBtn type="button" onClick={clearMedia} style={{ position: "static", marginTop: "8px", display: "inline-flex" }}>
                    ✕
                  </RemoveBtn>
                </MediaPreview>
              )}
              {mediaType === "audio" && (
                <MediaPreview style={{ display: "block" }}>
                  <audio src={mediaPreviewUrl} controls />
                  <RemoveBtn type="button" onClick={clearMedia} style={{ position: "static", marginTop: "8px", display: "inline-flex" }}>
                    ✕ 
                  </RemoveBtn>
                </MediaPreview>
              )}
              {(mediaType === "3d" || mediaType === "other") && (
                <MediaPreview style={{ display: "block" }}>
                  <FileDropZone $hasFile style={{ cursor: "default" }}>
                    <DropZoneIcon>📦</DropZoneIcon>
                    <DropZoneText $hasFile>
                      {mediaFile.name}
                    </DropZoneText>
                    <DropZoneHint>{(mediaFile.size / 1024 / 1024).toFixed(2)} MB</DropZoneHint>
                  </FileDropZone>
                  <RemoveBtn type="button" onClick={clearMedia} style={{ position: "static", marginTop: "8px", display: "inline-flex" }}>
                    ✕ 
                  </RemoveBtn>
                </MediaPreview>
              )}
              <MediaTypeTag>
                {mediaTypeLabel[mediaType] || "File"} · {mediaFile.name}
              </MediaTypeTag>

              {/* Change button */}
              <div style={{ position: "relative", display: "inline-block", marginTop: "8px" }}>
                <ChangeBtn type="button">Change</ChangeBtn>
                <input
                  type="file"
                  accept="image/*,video/*,audio/*,.glb,.gltf,.obj"
                  onChange={handleMediaChange}
                  disabled={isLoading}
                  style={{
                    position: "absolute",
                    inset: 0,
                    opacity: 0,
                    cursor: "pointer",
                    width: "100%",
                    height: "100%",
                  }}
                />
              </div>
            </div>
          )}
        </Section>

        <Divider />

        {/* ── Section 2: Preview image (only shown when media is non-image) ── */}
        {needsPreviewImg && (
          <>
            <Section>
              <SectionTitle>
                Preview Image <span className="required">*</span>
              </SectionTitle>
              <SectionDesc>
                Because you've included a {mediaTypeLabel[mediaType]?.toLowerCase()}, you'll
                need to provide an image (PNG, JPG, or GIF) for the card display of your
                item. Max size: {MAX_PREVIEW_SIZE_MB} MB.
              </SectionDesc>

              {!previewImgFile ? (
                <FileDropZone $hasFile={false}>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handlePreviewImgChange}
                    disabled={isLoading}
                  />
                  <DropZoneIcon>🖼️</DropZoneIcon>
                  <DropZoneText $hasFile={false}>
                    Drag & drop or <u>browse</u> your preview image
                  </DropZoneText>
                  <DropZoneHint>PNG, JPG, GIF supported</DropZoneHint>
                </FileDropZone>
              ) : (
                <MediaPreview>
                  <img src={previewImgUrl} alt="Preview" />
                  <RemoveBtn type="button" onClick={clearPreviewImg}>✕</RemoveBtn>
                </MediaPreview>
              )}
            </Section>
            <Divider />
          </>
        )}

        {/* ── Section 3: Name ── */}
        <Section>
          <SectionTitle>
            Name <span className="required">*</span>
          </SectionTitle>
          <SectionDesc>
            Give your NFT a unique and descriptive name that represents your artwork.
          </SectionDesc>
          <input
            type="text"
            placeholder="e.g. Cosmic Bloom #001"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
        </Section>

        <Divider />

        {/* ── Section 4: Price ── */}
        <Section>
          <SectionTitle>
            Price in ETH <span className="required">*</span>
          </SectionTitle>
          <SectionDesc>
            Set the listing price for your NFT in Ether (ETH). You can always update this
            later.
          </SectionDesc>
          <input
            type="number"
            step="any"
            min="0"
            placeholder="e.g. 0.05"
            value={price || ""}
            onChange={(e) => setPrice(parseFloat(e.target.value) || 0)}
            required
          />
        </Section>

        <Divider />

        {/* ── Section 5: Size ── */}
        <Section>
          <SectionTitle>
            Size{" "}
            <span className="optional-badge">Optional</span>
          </SectionTitle>
          <SectionDesc>
            Physical or digital dimensions of the artwork, e.g. "60 x 60 cm" or "4096 x
            4096 px".
          </SectionDesc>
          <input
            type="text"
            placeholder="e.g. 60 x 60 cm"
            value={size}
            onChange={(e) => setSize(e.target.value)}
          />
        </Section>

        <Divider />

        {/* ── Section 6: Description ── */}
        <Section>
          <SectionTitle>
            Description <span className="required">*</span>
          </SectionTitle>
          <SectionDesc>
            The description will be included on the item's detail page. Tell the story
            behind your artwork — inspiration, medium, process, or anything that adds
            meaning.
          </SectionDesc>
          <textarea
            placeholder={
              club
                ? `Describe your art for ${club.name}...`
                : "Describe your artwork..."
            }
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={4}
            required
          />
        </Section>

        <Divider />

        {/* ── Section 7: Royalties ── */}
        <Section>
          <SectionTitle>
            Creator Royalty{" "}
            <span className="optional-badge">Optional</span>
          </SectionTitle>
          <SectionDesc>
            Earn a percentage of the sale price every time your NFT is resold on a
            secondary market. Set to 0% to disable royalties.
          </SectionDesc>

          <RoyaltyRow>
            <RoyaltySlider
              type="range"
              min="0"
              max="15"
              step="0.5"
              value={royalty}
              onChange={(e) => setRoyalty(parseFloat(e.target.value))}
            />
            <RoyaltyValue>{royalty}%</RoyaltyValue>
          </RoyaltyRow>

          <RoyaltyPresets>
            {ROYALTY_PRESETS.map((preset) => (
              <PresetChip
                key={preset}
                type="button"
                $active={royalty === preset}
                onClick={() => setRoyalty(preset)}
              >
                {preset === 0 ? "No royalty" : `${preset}%`}
              </PresetChip>
            ))}
          </RoyaltyPresets>

          <RoyaltyInfo>
            💡 Most creators set between <strong>2.5% – 10%</strong>. A higher royalty may
            discourage resellers. Royalties are automatically paid to your wallet on each
            secondary sale.
          </RoyaltyInfo>
        </Section>

        <Divider />

        {/* ── Submit ── */}
        <SubmitButton type="submit" disabled={isLoading || !isSubmittable}>
          {isLoading ? (
            <LoaderIcon width={20} height={20} color="white" />
          ) : club ? (
            `Upload to ${club.name} Club`
          ) : (
            "Upload NFT"
          )}
        </SubmitButton>
      </form>
    </Wrapper>
  );
};